<script setup lang="ts">
defineProps<{
  /** Pixel size of the spinner. Default 48. */
  size?: number;
  /** Optional caption rendered under the spinner. */
  label?: string;
}>();
</script>

<template>
  <div class="lucent-spinner" role="status" aria-live="polite">
    <svg
      :width="size ?? 48"
      :height="size ?? 48"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <line class="ln ln-a" x1="6" y1="8" x2="14" y2="6" stroke="currentColor" stroke-width="0.6" />
      <line class="ln ln-b" x1="14" y1="6" x2="12" y2="14" stroke="currentColor" stroke-width="0.6" />
      <line class="ln ln-c" x1="12" y1="14" x2="20" y2="18" stroke="currentColor" stroke-width="0.6" />
      <circle class="dot dot-a" cx="6" cy="8" r="1.3" fill="currentColor" />
      <circle class="dot dot-b" cx="14" cy="6" r="1.3" fill="currentColor" />
      <circle class="dot dot-core" cx="12" cy="14" r="2" fill="currentColor" />
      <circle class="dot dot-c" cx="20" cy="18" r="1.3" fill="currentColor" />
    </svg>
    <span v-if="label" class="caption">{{ label }}</span>
  </div>
</template>

<style scoped>
.lucent-spinner {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: var(--primary, #6aceb0);
}
.caption {
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 11px;
  letter-spacing: 0.08em;
  color: var(--card-muted, #76767e);
  text-transform: uppercase;
}

/* Each satellite dot pulses on a stagger; the core stays steady-bright;
 * the connecting lines breathe with their downstream dot. */
.dot {
  transform-origin: center;
  transform-box: fill-box;
}
.dot-core {
  animation: lucent-core 1.6s ease-in-out infinite;
}
.dot-a {
  animation: lucent-pulse 1.6s ease-in-out infinite;
  animation-delay: 0s;
}
.dot-b {
  animation: lucent-pulse 1.6s ease-in-out infinite;
  animation-delay: 0.2s;
}
.dot-c {
  animation: lucent-pulse 1.6s ease-in-out infinite;
  animation-delay: 0.4s;
}
.ln {
  opacity: 0.35;
}
.ln-a {
  animation: lucent-line 1.6s ease-in-out infinite;
  animation-delay: 0.05s;
}
.ln-b {
  animation: lucent-line 1.6s ease-in-out infinite;
  animation-delay: 0.25s;
}
.ln-c {
  animation: lucent-line 1.6s ease-in-out infinite;
  animation-delay: 0.45s;
}

@keyframes lucent-pulse {
  0%,
  100% {
    opacity: 0.25;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.25);
  }
}
@keyframes lucent-core {
  0%,
  100% {
    opacity: 0.85;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}
@keyframes lucent-line {
  0%,
  100% {
    opacity: 0.15;
  }
  50% {
    opacity: 0.7;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dot,
  .ln {
    animation: none !important;
  }
}
</style>
